import React, { useEffect, useState } from 'react';
import { Platform, View, StyleSheet, Modal, Pressable } from 'react-native';
import { TextInput } from 'react-native-paper';
import { Calendar, registerTranslation, enGB, ja } from 'react-native-paper-dates';

// The calendar/input read locale strings from a registry that starts empty —
// unregistered locales degrade to raw keys (and warnings) on every render.
registerTranslation('en-GB', enGB);
registerTranslation('ja-JP', ja);
import { useAppColors } from '../hooks/useAppColors';
import { useTranslation } from 'react-i18next';

interface DatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  label?: string;
  error?: boolean;
  placeholder?: string;
  maximumDate?: Date;
  minimumDate?: Date;
}

export default function DatePicker({
  value,
  onChange,
  label,
  error = false,
  placeholder,
  maximumDate,
  minimumDate,
}: DatePickerProps) {
  const { i18n } = useTranslation();
  const colors = useAppColors();
  const [visible, setVisible] = useState(false);

  // Map language to locale format
  // Default (en, vi): DD/MM/YYYY
  // Japanese (ja): YYYY/MM/DD
  const getLocale = () => {
    const lang = i18n.language;
    if (lang === 'ja') return 'ja-JP';
    return 'en-GB'; // Uses DD/MM/YYYY format
  };

  const locale = getLocale();

  // On web, close the dialog when the user presses Escape.
  useEffect(() => {
    if (Platform.OS !== 'web' || !visible) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // stopImmediatePropagation so a parent modal's window listener doesn't
        // also close on the same Escape.
        e.stopImmediatePropagation();
        setVisible(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [visible]);

  const inputTheme = {
    colors: {
      onSurfaceVariant: colors.textSecondary,
      surface: colors.card,
      primary: colors.text,
      outline: colors.border,
    },
  };

  const validRange =
    minimumDate || maximumDate
      ? { startDate: minimumDate, endDate: maximumDate }
      : undefined;

  // Constrain the Calendar's year picker to only supported years so that
  // out-of-range years are not even shown in the list.
  const currentYear = new Date().getFullYear();
  const startYear = minimumDate ? minimumDate.getFullYear() : currentYear - 120;
  const endYear = maximumDate ? maximumDate.getFullYear() : currentYear + 50;

  // Default display date for the calendar: 20 years ago (for DOB fields).
  // Used when no date is selected so the calendar opens near a typical birth year
  // instead of defaulting to today.
  const defaultDisplayDate = new Date(currentYear - 20, 0, 1);

  // Selector-only field: display the chosen date, tap anywhere to open the
  // calendar. No free-text entry — members shouldn't have to guess a format.
  const formatDisplay = (d: Date) => {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return locale === 'ja-JP' ? `${yyyy}/${mm}/${dd}` : `${dd}/${mm}/${yyyy}`;
  };

  return (
    <View>
      <Pressable onPress={() => setVisible(true)}>
        <View pointerEvents="none">
          <TextInput
            mode="outlined"
            label={label}
            value={value ? formatDisplay(value) : ''}
            placeholder={placeholder}
            editable={false}
            error={error}
            style={styles.input}
            theme={inputTheme}
            right={<TextInput.Icon icon="calendar" forceTextInputFocus={false} />}
          />
        </View>
      </Pressable>

      {/* A native Modal (not Paper's Portal/Dialog) so the calendar stacks
          ABOVE any parent native Modal — a Portal renders beneath one. */}
      <Modal
        visible={visible}
        transparent
        animationType="fade"
        onRequestClose={() => setVisible(false)}
      >
        <Pressable style={styles.overlay} onPress={() => setVisible(false)}>
          <Pressable
            style={[styles.dialogCard, { backgroundColor: colors.card }]}
            onPress={(e) => e.stopPropagation?.()}
          >
            {/* Explicit height: the Calendar sizes itself with flex, which
                collapses to 0 inside a content-sized container on native. */}
            <View style={styles.calendarContainer}>
              <Calendar
                mode="single"
                locale={locale}
                date={value ?? defaultDisplayDate}
                validRange={validRange}
                startYear={startYear}
                endYear={endYear}
                onChange={({ date }) => {
                  // Fires the moment a date is tapped — auto-close.
                  onChange(date ?? null);
                  setVisible(false);
                }}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  input: {
    marginBottom: 12,
    backgroundColor: 'transparent',
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  dialogCard: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 16,
    padding: 8,
    overflow: 'hidden',
  },
  calendarContainer: {
    height: 400,
  },
});
